import InputError from '@/components/input-error';
import TextLink from '@/components/text-link';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import AuthLayout from '@/layouts/auth-layout';
import { Turnstile } from '@/components/turnstile';
import { store } from '@/routes/login';
import { request } from '@/routes/password';
import { Form, Head } from '@inertiajs/react';
import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface LoginProps {
    status?: string;
    canResetPassword: boolean;
}

export default function Login({
    status,
    canResetPassword,
}: LoginProps) {
    const { t } = useTranslation();
    const [showPassword, setShowPassword] = useState(false);

    return (
        <AuthLayout
            title="Aiunify Call Center"
            description="Manage live calling teams and telemarketing campaigns."
        >
            <Head title="Log in" />

            <Form
                {...store.form()}
                resetOnSuccess={['password']}
                className="flex flex-col gap-5"
            >
                {({ processing, errors }) => (
                    <>
                        {status && (
                            <div className="pbau-auth-status rounded-2xl px-4 py-3 text-center text-sm font-medium">
                                {status}
                            </div>
                        )}

                        <div className="grid gap-[18px] max-sm:gap-4">
                            <div className="grid gap-2.5 max-sm:gap-2">
                                <Label htmlFor="email" className="pbau-auth-label max-sm:text-[12px]">{t('auth.email_address')}</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    name="email"
                                    required
                                    autoFocus
                                    tabIndex={1}
                                    autoComplete="email"
                                    placeholder=" "
                                    className="pbau-auth-input px-[14px] text-[15px] max-sm:px-3 max-sm:text-[14px]"
                                />
                                <InputError message={errors.email} />
                            </div>

                            <div className="grid gap-2.5 max-sm:gap-2">
                                <div className="flex items-center">
                                    <Label htmlFor="password" className="pbau-auth-label max-sm:text-[12px]">{t('auth.password')}</Label>
                                    {canResetPassword && (
                                        <TextLink
                                            href={request()}
                                            className="ml-auto"
                                            tabIndex={5}
                                        >
                                            Forgot password?
                                        </TextLink>
                                    )}
                                </div>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        name="password"
                                        required
                                        tabIndex={2}
                                        autoComplete="current-password"
                                        placeholder=" "
                                        className="pbau-auth-input px-[14px] pe-12 text-[15px] max-sm:px-3 max-sm:pe-11 max-sm:text-[14px]"
                                    />
                                    <button
                                        type="button"
                                        className="pbau-auth-password-toggle"
                                        onClick={() => setShowPassword((value) => !value)}
                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                        tabIndex={-1}
                                    >
                                        {showPassword ? (
                                            <EyeOff className="size-4" />
                                        ) : (
                                            <Eye className="size-4" />
                                        )}
                                    </button>
                                </div>
                                <InputError message={errors.password} />
                            </div>

                            <div className="flex items-center gap-[9px]">
                                <Checkbox
                                    id="remember"
                                    name="remember"
                                    tabIndex={3}
                                    className="pbau-auth-checkbox"
                                />
                                <Label htmlFor="remember" className="pbau-auth-check-label">Remember me</Label>
                            </div>

                            <Turnstile />

                            <Button
                                type="submit"
                                className="pbau-auth-submit mt-1 w-full px-6 text-[15px] font-extrabold max-sm:px-[18px] max-sm:text-[14px]"
                                tabIndex={4}
                                disabled={processing}
                                data-test="login-button"
                            >
                                {processing && <Spinner />}
                                Log in
                            </Button>
                        </div>
                    </>
                )}
            </Form>
        </AuthLayout>
    );
}
